import { memo, useMemo } from 'react'
import { useIntl } from 'react-intl'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import OfferBookButton from 'components/OfferPage/OfferBookButtons/OfferBookButton'
import config from 'constants/config'
import Group from 'components/utils/Group'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { useAppSelector } from 'hooks/reduxHooks'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import OfferAddToCartButton from '../OfferBookButtons/OfferAddToCartButton'
import AlwaysOnMarginText from './AlwaysOnMarginText'
import OfferSelectPackageButton from '../OfferBookButtons/OfferSelectPackageButton'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import PriceRowLuxPlusNonMemberPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusNonMemberPrice'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'

interface Props {
  packageView: App.OfferPackageOptionView
  rooms?: Array<App.Occupants>
  offer: App.Offer
  checkIn?: string
  isFlightPrice?: boolean
  canBuyNow?: boolean
  valuedUpTo: {
    value: number
    percent: number
  }
}

function AlwaysOnPriceDetails(props: Props) {
  const {
    rooms,
    offer,
    checkIn,
    isFlightPrice,
    canBuyNow,
    valuedUpTo,
    packageView,
  } = props

  const canAddToUserCart = canBuyNow

  const pkg = packageView.package

  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])

  const hotelPrice = packageView.hotelPrice

  const propertyFees = packageView.propertyFees
  let totalPrice = (isFlightPrice ? hotelPrice + packageView.flightPrice : hotelPrice)
  totalPrice = totalPrice + propertyFees
  const totalValuedUp = valuedUpTo.value + (isFlightPrice ? packageView.flightPrice : 0) + propertyFees
  const showPriceDetails = packageView.taxesAndFees > 0 || propertyFees || config.agentHub.isEnabled
  const shouldShowValue = valuedUpTo.value > packageView.hotelPrice && !pkg.roomRate?.inclusionsHideValue
  const isFromPrice = isFlightPrice || !checkIn

  const margin = pkg.roomRate?.marginPercentage
  const marginValue = margin ? hotelPrice * margin / 100 : 0
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const intl = useIntl()
  const isLuxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const showMarginInSpoofMode = useAppSelector(state => state.system.showMarginInSpoofMode)
  const displayMargin = Boolean(isSpoofed && showMarginInSpoofMode && margin && marginValue)

  const memberPrice = packageView.hotelMemberPrice
  const shouldShowLuxPlusPricing = isLuxPlusEnabled && typeof memberPrice === 'number' && memberPrice > 0
  const priceCaption = (() => {
    if (isFlightPrice) {
      return intl.formatMessage({
        id: '92CjARDYkD4Re0b65kU8',
        defaultMessage: '{duration} + flights from',
        description: 'Price caption with duration, flights indicator, and from prefix on an offer',
      }, { duration: pkg.durationLabel.toLowerCase() })
    }
    if (isFromPrice) {
      return intl.formatMessage({
        id: 'm4PessNH4R8W013jh2Om',
        defaultMessage: '{duration} from',
        description: 'Price caption with duration and from prefix on an offer',
      }, { duration: pkg.durationLabel.toLowerCase() })
    }
    return pkg.durationLabel.toLowerCase()
  })()
  const extraGuestSurchargeLabel = intl.formatMessage({
    id: 'Xv3DHb9kZvyCEYIlgCcx',
    defaultMessage: '+ Extra guest surcharge',
    description: 'Caption shown on always-on offer pricing when an extra guest surcharge applies',
  })

  return <Group direction="vertical" gap={4}>
    <div>
      <PriceRowPriceCaption>
        {isFlightPrice && <LinePlaneDepartureIcon size="S" />} {priceCaption}
      </PriceRowPriceCaption>
      {shouldShowLuxPlusPricing && (
        <>
          <PriceRowLuxPlusPrice
            memberPrice={memberPrice}
            saleUnit="total"
            logoPlacement="trailing"
            size="S"
            signDisplay="auto"
          />
          <PriceRowLuxPlusNonMemberPrice
            nonMemberPrice={totalPrice}
            signDisplay="auto"
          />
        </>
      )}
      {!shouldShowLuxPlusPricing && (
        <PriceRowPrice
          data-testid="always-on-package-price"
          size="S"
          price={totalPrice}
          saleUnit="total"
        />
      )}
      <PriceRowTaxesAndFees />
      {shouldShowValue && <PriceRowValueDiscountWithCaption
        size="M"
        originalValue={totalValuedUp}
        data-testid="always-on-price-details-value"
      />}
    </div>
    {showIncludedGuestsExceeded && <AdditionalGuestsPopup
      complex={false}
      description={extraGuestSurchargeLabel}
      modalContent={pkg.roomPolicyDescription}
    />}
    {showPriceDetails && <OfferPriceDetailsRow
      trigger="price-row"
      triggerSize="M"
      offerPage
      offer={offer}
      duration={pkg.duration}
      propertyFees={packageView.propertyFees}
      hotelPrice={packageView.hotelPrice}
      taxesAndFees={packageView.taxesAndFees}
      flightPrice={isFlightPrice ? packageView.flightPrice : undefined}
      rooms={rooms}
      extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
      dueAtPropertyMessage={offer.property?.taxesAndFeesContent ?? ''}
      showMemberPrice={shouldShowLuxPlusPricing}
      hotelMemberPrice={memberPrice}
      cta={<Group
        direction="vertical"
        tabletDirection="horizontal"
        horizontalAlign="stretch"
        tabletHorizontalAlign="end"
        gap={8}
      >
        {canAddToUserCart && !isFlightPrice && <OfferAddToCartButton
          size="large"
          type="submit"
          packageView={packageView}
        />}
        {canAddToUserCart && isFlightPrice && <OfferSelectPackageButton
          size="large"
          data-buytype="flights"
          packageView={packageView}
        />}
        {canBuyNow && !isFlightPrice && <OfferBookButton
          size="large"
          type="submit"
          data-buytype={isFlightPrice ? 'flights' : undefined}
          packageView={packageView}
        >
          {intl.formatMessage({
            id: 'rYCF71qK7e_ziZB6LQxt',
            defaultMessage: 'Book now',
            description: 'Button to proceed with booking on always-on offer price details',
          })}
        </OfferBookButton>}
      </Group>}
    />}
    {displayMargin && <AlwaysOnMarginText
      margin={margin ?? 0}
      marginValue={marginValue}
      type="ao_estimate"
    />}
  </Group>
}

export default memo(AlwaysOnPriceDetails)
